import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { Drawer, Icons, Typography } from '../../stories/atoms';
import {
  useAsyncAction,
  useLocalStorage,
  useStoreInformation,
  useStores,
  useUser,
  WeatherResponse,
} from '../../hooks';
import { homeTranslations } from '../../translations';
import { isFailureResponse } from '../../utils';
import { LocalStorageKey } from '../../constants/localStorageKey';
import { StyledHeader, StyledIcon } from './HomepageSlider.styles';
import { WeatherWidget } from './weatherWidget/WeatherWidget';
import { StoreOpeningHoursWidget } from './storeOpeningHoursWidget/StoreOpeningHoursWidget';
import { MyStoreMap } from './myStoreMap/MyStoreMap';

export type HomePageSliderProps = {
  isOpen: boolean;
  onClose?: () => void;
};

export const HomePageSlider: FC<React.PropsWithChildren<HomePageSliderProps>> = ({ isOpen, onClose }) => {
  const { fetchWeatherWidgetData } = useStores();
  const { areaName } = useStoreInformation();
  const {
    get: { currentStoreId },
  } = useUser();
  const { getItem } = useLocalStorage();

  const selectedView = getItem(LocalStorageKey.SelectedView);
  const isArea = selectedView === 'area';

  const {
    result: weatherResponse,
    isError: weatherResponseError,
    triggerAction: triggerFetchWeatherResponse,
  } = useAsyncAction<WeatherResponse>();

  const [openingHoursDetails, setOpeningHoursDetail] = useState<{
    isError: boolean;
  }>({
    isError: false,
  });

  const [isSliderOpen, setIsSliderOpen] = useState(isOpen);

  useEffect(() => {
    setIsSliderOpen(isOpen);
  }, [isOpen]);

  const fetchWeatherResponse = async () => {
    const response = await fetchWeatherWidgetData();

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };
  useEffect(() => {
    if (!(isOpen && currentStoreId)) return;

    triggerFetchWeatherResponse(fetchWeatherResponse);
  }, [currentStoreId, isOpen]);

  useEffect(() => {
    setOpeningHoursDetail({ isError: false });
  }, [currentStoreId]);

  const { slider } = homeTranslations;
  const { t } = useTranslation();

  const handleClose = () => {
    setIsSliderOpen(false);
    if (onClose) onClose();
  };

  return (
    <Drawer variant="temporary" position="right" isOpen={isSliderOpen} onClose={() => handleClose()}>
      <Box data-testid="slider" sx={{ width: 350 }}>
        <StyledHeader>
          <div>
            <Typography type="h4">
              {t(slider.generalInformation.key, slider.generalInformation.defaultValue)}
            </Typography>
            <Typography color="secondaryTextGray">{isArea ? areaName : currentStoreId}</Typography>
          </div>
          <StyledIcon data-testid="closeIcon" icon={Icons.CLOSE} size="small" onClick={handleClose} />
        </StyledHeader>
        <MyStoreMap isArea={isArea} areaName={areaName} />
        <WeatherWidget data={weatherResponse} isError={weatherResponseError} />
        {!isArea && <StoreOpeningHoursWidget isError={openingHoursDetails.isError}></StoreOpeningHoursWidget>}
      </Box>
    </Drawer>
  );
};
